'use client';

import { cn } from '@core/utils';
import type { EmblaOptionsType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useImperativeHandle } from 'react';

type AutoplayOptions = {
  delay?: number;
  stopOnInteraction?: boolean;
};

interface HorizontalScrollProps {
  children: ReactNode;
  className?: string;
  slideClassName?: string;
  /** 슬라이드 변경 시 콜백 */
  onSlideChange?: (index: number) => void;
  /** 스크롤 프로그레스 콜백 (0-1) */
  onProgress?: (progress: number) => void;
  /** 무한 루프 활성화 */
  loop?: boolean;
  /** 자동 재생 옵션 */
  autoplay?: boolean | AutoplayOptions;
  /** 슬라이드 정렬 */
  align?: 'start' | 'center' | 'end';
  /** 드래그 자유도 */
  dragFree?: boolean;
  /** API 접근을 위한 ref */
  carouselRef?: React.RefObject<CarouselApi | null>;
}

export interface CarouselApi {
  scrollNext: () => void;
  scrollPrev: () => void;
  scrollTo: (index: number) => void;
  selectedScrollSnap: () => number;
  canScrollNext: () => boolean;
  canScrollPrev: () => boolean;
}

export const HorizontalScroll = ({
  children,
  className,
  slideClassName,
  onSlideChange,
  onProgress,
  loop = false,
  autoplay = false,
  align = 'start',
  dragFree = true,
  carouselRef,
}: HorizontalScrollProps) => {
  // Embla 옵션 구성
  const options: EmblaOptionsType = {
    loop,
    align,
    dragFree: !loop && dragFree, // loop 모드에서는 dragFree 비활성화
    containScroll: loop ? false : 'trimSnaps',
  };

  const plugins = autoplay
    ? [
        Autoplay(
          typeof autoplay === 'object'
            ? autoplay
            : { delay: 5000, stopOnInteraction: false },
        ),
      ]
    : [];

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  const getAutoplay = useCallback(() => {
    if (!emblaApi) return undefined;

    return emblaApi.plugins()?.autoplay as
      | ReturnType<typeof Autoplay>
      | undefined;
  }, [emblaApi]);

  const resetAutoplay = useCallback(() => {
    const autoplayPlugin = getAutoplay();
    autoplayPlugin?.reset?.();
  }, [getAutoplay]);

  // API 노출
  useImperativeHandle(carouselRef, () => {
    if (!emblaApi) {
      return {
        scrollNext: () => {},
        scrollPrev: () => {},
        scrollTo: () => {},
        selectedScrollSnap: () => 0,
        canScrollNext: () => false,
        canScrollPrev: () => false,
      };
    }

    return {
      scrollNext: () => {
        emblaApi.scrollNext();
        resetAutoplay();
      },
      scrollPrev: () => {
        emblaApi.scrollPrev();
        resetAutoplay();
      },

      scrollTo: (index: number) => emblaApi.scrollTo(index),
      selectedScrollSnap: () => emblaApi.selectedScrollSnap(),
      canScrollNext: () => emblaApi.canScrollNext(),
      canScrollPrev: () => emblaApi.canScrollPrev(),
    };
  }, [emblaApi, resetAutoplay]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    onSlideChange?.(index);
  }, [emblaApi, onSlideChange]);

  const onScroll = useCallback(() => {
    if (!emblaApi || !onProgress) return;
    const progress = emblaApi.scrollProgress();
    onProgress(progress);
  }, [emblaApi, onProgress]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('scroll', onScroll);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('scroll', onScroll);
    };
  }, [emblaApi, onSelect, onScroll]);

  return (
    <div ref={emblaRef} className={cn('overflow-hidden', className)}>
      <div className={cn('flex', slideClassName)}>{children}</div>
    </div>
  );
};

HorizontalScroll.displayName = 'HorizontalScroll';
